import React from "react";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import {
  FaPlus,
  FaSuitcase,
  FaCheck,
  FaUsers,
  FaClock,
  FaEdit,
  FaTrash,
  FaMapMarkerAlt,
} from "react-icons/fa";
import moment from "moment";
import HTMLContent from "../../common/HtmlContent";

function Tours({
  handleCreateTour,
  tours,
  toursData,
  tourBookings,
  tourBookingsData,
  handleEditTour,
  fetchData,
}) {
  return (
    <>
      <div className="bg-gray-100 p-6 rounded-lg shadow-md border border-gray-200 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Tour Management</h3>
          <motion.button
            onClick={handleCreateTour}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 shadow-lg flex items-center"
          >
            <FaPlus className="mr-2" />
            <span>Create New Tour</span>
          </motion.button>
        </div>

        {/* Tours Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-6 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-indigo-400 bg-opacity-40">
                <FaSuitcase className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-white text-sm opacity-80">Total Tours</p>
                <p className="text-2xl font-bold text-white">
                  {toursData.length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
                <FaCheck className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-white text-sm opacity-80">Active</p>
                <p className="text-2xl font-bold text-white">
                  {toursData.filter((tour) => tour.isActive).length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-purple-400 bg-opacity-40">
                <FaUsers className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-white text-sm opacity-80">Booked Tours</p>
                <p className="text-2xl font-bold text-white">
                  {tourBookingsData.length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-amber-500 to-amber-700 p-6 rounded-xl shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-amber-400 bg-opacity-40">
                <FaClock className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-white text-sm opacity-80">Pending Tours</p>
                <p className="text-2xl font-bold text-white">
                  {
                    tourBookingsData.filter(
                      (booking) => booking.status === "pending"
                    ).length
                  }
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tours List */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Available Tours
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {toursData.map((tour) => (
              <motion.div
                key={tour._id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
                className={`bg-white rounded-xl shadow-md border overflow-hidden transition-all duration-300 ${
                  !tour.isActive ? "opacity-60" : ""
                }`}
              >
                <div className="relative h-48 bg-blue-50">
                  {tour.imageUrl ? (
                    <img
                      src={
                        tour.imageUrl.startsWith("http")
                          ? tour.imageUrl
                          : `${"https://rent-server.create-site.gr"}${
                              tour.imageUrl.startsWith("/") ? "" : "/"
                            }${tour.imageUrl}`
                      }
                      alt={tour.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <FaSuitcase className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                  <div className="absolute top-4 right-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        tour.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {tour.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex justify-between items-start">
                    <h5 className="text-xl font-bold text-gray-900 mb-2">
                      {tour.name}
                    </h5>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEditTour(tour._id)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          Swal.fire({
                            title: "Delete Tour?",
                            text: "This will permanently delete this tour. Are you sure?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#d33",
                            cancelButtonColor: "#3085d6",
                            confirmButtonText: "Yes, delete it!",
                          }).then((result) => {
                            if (result.isConfirmed) {
                              tours
                                .delete(tour._id)
                                .then(() => {
                                  Swal.fire(
                                    "Deleted!",
                                    "The tour has been deleted.",
                                    "success"
                                  );
                                  fetchData(); // Refresh tour data
                                })
                                .catch((error) => {
                                  console.error("Error deleting tour:", error);
                                  Swal.fire({
                                    icon: "error",
                                    title: "Deletion Failed",
                                    text: "Failed to delete tour. Please try again.",
                                    confirmButtonColor: "#1e3a8a",
                                  });
                                });
                            }
                          });
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-2">
                    <span className="text-sm text-gray-500">
                      {tour.duration} hours • Max {tour.maxPersons} persons
                    </span>
                    <p className="mt-2 text-sm text-gray-600 line-clamp-3">
                      <HTMLContent html={tour.description} />
                    </p>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                      <FaMapMarkerAlt className="mr-1 w-3 h-3" />
                      <HTMLContent html={tour.locations} />
                    </span>

                    {tour.locations.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                        +{tour.locations.length - 3} more
                      </span>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-gray-500">Starting from</p>
                        <div>
                          {(() => {
                            // Determine current season based on today's date
                            const today = new Date();
                            const month = today.getMonth() + 1; // JavaScript months are 0-based
                            const day = today.getDate();

                            // Default to low season if not matched
                            let currentSeason = "low";

                            // Check if current date falls within medium season
                            if (
                              (month === 6 && day >= 26) ||
                              (month === 7 && day <= 10) ||
                              (month === 8 && day >= 26) ||
                              (month === 9 && day <= 15)
                            ) {
                              currentSeason = "medium";
                            }
                            // Check if current date falls within high season
                            else if (
                              (month === 7 && day >= 11) ||
                              (month === 8 && day <= 25)
                            ) {
                              currentSeason = "high";
                            }

                            // Get price based on vehicle type and season
                            const price =
                              tour.vehicleType === "car"
                                ? tour.pricing.car[`${currentSeason}Season`]
                                : tour.vehicleType === "minivan"
                                ? tour.pricing.minivan[`${currentSeason}Season`]
                                : tour.vehicleType === "van"
                                ? tour.pricing.van[`${currentSeason}Season`]
                                : tour.pricing.minibus[
                                    `${currentSeason}Season`
                                  ];

                            return (
                              <>
                                <p className="text-lg font-bold text-blue-600">
                                  €{price}
                                </p>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          tours
                            .toggleActive(tour._id)
                            .then(() => {
                              Swal.fire({
                                icon: "success",
                                title: "Tour Status Updated!",
                                text: `Tour has been ${
                                  tour.isActive ? "deactivated" : "activated"
                                }.`,
                                confirmButtonColor: "#1e3a8a",
                              });
                              fetchData(); // Refresh tour data
                            })
                            .catch((error) => {
                              console.error(
                                "Error toggling tour status:",
                                error
                              );
                              Swal.fire({
                                icon: "error",
                                title: "Update Failed",
                                text: "Failed to update tour status. Please try again.",
                                confirmButtonColor: "#1e3a8a",
                              });
                            });
                        }}
                        className={`px-4 py-2 text-xs font-medium rounded-lg ${
                          tour.isActive
                            ? "bg-red-100 text-red-800 hover:bg-red-200"
                            : "bg-green-100 text-green-800 hover:bg-green-200"
                        }`}
                      >
                        {tour.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Tour Bookings */}
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Tour Bookings
          </h4>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "Tour",
                    "Date & Time",
                    "Persons",
                    "Price",
                    "Vehicle",
                    "Pickup Location",
                    "Payment",
                    "Travel Guide",
                    "Customer",
                    "Status",
                    "Actions",
                  ].map((header) => (
                    <th
                      key={header}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tourBookingsData.map((booking) => {
                  return (
                    <tr key={booking._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {booking.tour?.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {moment(booking.tourDate).format("MMM D, YYYY")}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {booking.numberOfPersons}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          €{booking.totalPrice}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">
                          {booking.vehicleType}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {booking.pickupLocation}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">
                          {booking.paymentMethod?.replace("-", " ")}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {booking.travelGuide.available
                            ? "Yes, for " + booking.travelGuide.time
                            : "No"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          className="px-3 py-1 rounded bg-blue-100 text-blue-800 text-xs font-semibold hover:bg-blue-200 transition"
                          onClick={() => {
                            Swal.fire({
                              title: "Customer Information",
                              html: `
                              <div class="text-left space-y-2">
                                <div>
                                  <span class="font-semibold">Name:</span>
                                  <span>${booking.name || ""}</span>
                                </div>
                                <div>
                                  <span class="font-semibold">Email:</span>
                                  <span>${booking.email || ""}</span>
                                </div>
                                <div>
                                  <span class="font-semibold">Phone:</span>
                                  <span>${booking.phone || ""}</span>
                                </div>
                                <div>
                                  <span class="font-semibold">Notes:</span>
                                  <span>${booking.notes || "-"}</span>
                                </div>
                                <div>
                                  <span class="font-semibold">Created:</span>
                                  <span>${moment(booking.createdAt).format(
                                    "MMM D, YYYY HH:mm"
                                  )}</span>
                                </div>
                              </div>
                            `,
                              confirmButtonColor: "#1e3a8a",
                            });
                          }}
                        >
                          View
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${
                            booking.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : booking.status === "confirmed"
                              ? "bg-blue-100 text-blue-800"
                              : booking.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {booking.status.charAt(0).toUpperCase() +
                            booking.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <select
                            value={booking.status}
                            onChange={(e) => {
                              tourBookings
                                .updateStatus(booking._id, e.target.value)
                                .then(() => {
                                  Swal.fire({
                                    icon: "success",
                                    title: "Status Updated!",
                                    text: `Booking status changed.`,
                                    confirmButtonColor: "#1e3a8a",
                                    timer: 1500,
                                  });
                                  fetchData();
                                })
                                .catch((error) => {
                                  console.error(
                                    "Error updating booking status:",
                                    error
                                  );
                                  Swal.fire({
                                    icon: "error",
                                    title: "Update Failed",
                                    text: "Failed to update booking status.",
                                    confirmButtonColor: "#1e3a8a",
                                  });
                                });
                            }}
                            className="text-sm border-gray-300 rounded-lg p-2"
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <button
                            onClick={() => {
                              Swal.fire({
                                title: "Delete Booking?",
                                text: "Are you sure you want to delete this tour booking?",
                                icon: "warning",
                                showCancelButton: true,
                                confirmButtonColor: "#d33",
                                cancelButtonColor: "#3085d6",
                                confirmButtonText: "Yes, delete it!",
                              }).then((result) => {
                                if (result.isConfirmed) {
                                  tourBookings
                                    .delete(booking._id)
                                    .then(() => {
                                      Swal.fire(
                                        "Deleted!",
                                        "The booking has been deleted.",
                                        "success"
                                      );
                                      fetchData();
                                    })
                                    .catch((error) => {
                                      console.error(
                                        "Error deleting booking:",
                                        error
                                      );
                                      Swal.fire({
                                        icon: "error",
                                        title: "Deletion Failed",
                                        text: "Failed to delete booking.",
                                        confirmButtonColor: "#1e3a8a",
                                      });
                                    });
                                }
                              });
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {tourBookingsData.length === 0 && (
                  <tr>
                    <td
                      colSpan="10"
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No tour bookings found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Tours;
